import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import { PurchaseOrderTable } from './PurchaseOrderTable';
import UploadModal from './upload_modal.component';
import UploadRingsDetailModal from './upload_rings_detail_modal.component';
import EditPurchaseOrderModal from './edit_purchase_order_modal.component';
import { uploadPurchaseOrderExcel } from './functions/upload_purchase_order_excel.function';
import { uploadPoRingsDetailExcel } from './functions/upload_po_rings_detail_excel.function';
import { notify } from '../../common/functions/react-toastify.function';
import styles from './purchase_order_management.module.css';

const PurchaseOrderManagement = () => {
  const navigate = useNavigate();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [isRingsDetailModalOpen, setIsRingsDetailModalOpen] = useState(false);
  const [ringsDetailSelectedPO, setRingsDetailSelectedPO] = useState(null);
  const [isRingsDetailUploading, setIsRingsDetailUploading] = useState(false);
  const purchaseOrderTableRef = useRef(null);
  const permissionNames = useMemo(() => {
    try {
      const stored = localStorage.getItem('permissions');
      if (!stored) {
        return [];
      }
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map((p) => p.name) : [];
    } catch (error) {
      console.error('Failed to parse permissions:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!permissionNames.includes('Admin Invoice Management')) {
      navigate('/rov', { replace: true });
    }
  }, [permissionNames, navigate]);

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadFile = async (file) => {
    if (!file) {
      notify('error', 'Please select a file to upload');
      return;
    }

    setIsUploading(true);
    try {
      const result = await uploadPurchaseOrderExcel(file);
      if (result.success) {
        notify('success', result.message || 'File uploaded successfully');

        // If API returns saved records, merge them; otherwise refetch so UI updates immediately.
        if (
          Array.isArray(result.data) &&
          result.data.length > 0 &&
          purchaseOrderTableRef.current?.addOrUpdatePurchaseOrders
        ) {
          purchaseOrderTableRef.current.addOrUpdatePurchaseOrders(result.data);
        } else if (purchaseOrderTableRef.current?.refetchPurchaseOrders) {
          await purchaseOrderTableRef.current.refetchPurchaseOrders();
        }

        setIsUploadModalOpen(false);
      }
    } catch (error) {
      console.error('Upload error:', error);
      notify('error', 'Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditClick = (purchaseOrder) => {
    setSelectedPurchaseOrder(purchaseOrder);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedPurchaseOrder(null);
  };

  const handleUploadRingsDetailClick = (row) => {
    setRingsDetailSelectedPO({ id: row.id, po: row.po });
    setIsRingsDetailModalOpen(true);
  };

  const handleRingsDetailModalClose = () => {
    setIsRingsDetailModalOpen(false);
    setRingsDetailSelectedPO(null);
  };

  const handleRingsDetailUpload = async (file) => {
    if (!ringsDetailSelectedPO || !file) {
      notify('error', 'Please select a file to upload');
      return;
    }
    setIsRingsDetailUploading(true);
    try {
      const result = await uploadPoRingsDetailExcel(ringsDetailSelectedPO.id, file);
      if (result.success) {
        notify('success', result.message || 'Rings detail uploaded successfully');
        handleRingsDetailModalClose();
      }
    } catch (error) {
      console.error('Rings detail upload error:', error);
      notify('error', 'Failed to upload rings detail. Please try again.');
    } finally {
      setIsRingsDetailUploading(false);
    }
  };

  const handleUpdateSuccess = (updatedData) => {
    // Update the record in the table using the update API response
    if (purchaseOrderTableRef.current?.updatePurchaseOrder) {
      purchaseOrderTableRef.current.updatePurchaseOrder(updatedData);
    }
  };

  const handleBackClick = () => {
    navigate('/rov');
  };

  return (
    <>
      <DashboardLayout padding={true}>
        <div className={styles['purchase-order-container']}>
          <div className={styles['purchase-order-header']}>
            <div className={styles['header-left']}>
              <div className={styles['back-icon-container']} onClick={handleBackClick}>
                <ArrowBackIosOutlinedIcon className={styles['back-icon']} />
              </div>
              <h1 className={styles['page-title']}>Purchase Order Management</h1>
            </div>
            <button className={styles['upload-btn']} onClick={handleUploadClick}>
              Upload
            </button>
          </div>

          <PurchaseOrderTable
            ref={purchaseOrderTableRef}
            onEdit={handleEditClick}
            onUploadRingsDetail={handleUploadRingsDetailClick}
            onDeleteSuccess={() => {
              // Optional: Add any additional logic after successful deletion
              console.log('Purchase order deleted successfully');
            }}
          />

          {/* Upload Modal */}
          <UploadModal
            isOpen={isUploadModalOpen}
            onClose={() => setIsUploadModalOpen(false)}
            onUpload={handleUploadFile}
            isUploading={isUploading}
          />

          {/* Edit Modal */}
          <EditPurchaseOrderModal
            isOpen={isEditModalOpen}
            onClose={handleEditModalClose}
            purchaseOrder={selectedPurchaseOrder}
            onUpdateSuccess={handleUpdateSuccess}
          />

          {/* Upload rings detail Modal */}
          <UploadRingsDetailModal
            isOpen={isRingsDetailModalOpen}
            onClose={handleRingsDetailModalClose}
            onUpload={handleRingsDetailUpload}
            isUploading={isRingsDetailUploading}
            selectedPO={ringsDetailSelectedPO}
          />
        </div>
      </DashboardLayout>
    </>
  );
};

export { PurchaseOrderManagement };
